import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import { createConsentTypeAction, updateConsentTypeAction } from "../../actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import useHandleForm from "./useHandleForm";
import GalleryDialog from "@icarius-common/galleryDialog/components/index";
import GalleryButtonAndDisplay from "@icarius-common/galleryDialog/components/galleryButtonAndDisplay";
import { getTypeList, getInstanceList } from "../../selectors";
import FileList from "@icarius-common/fileUpload/fileList";
import FileInput from "@icarius-common/fileUpload/fileInput";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";

const gridStyle = { height: 75, padding: "0px 10px" };

const Form = (props) => {

  const { data, handleClose } = props;

  const dispatch = useDispatch();
  const types = useSelector(getTypeList);
  const instances = useSelector(getInstanceList);

  const [imagesDialogIsOpen, setImagesDialogIsOpen] = useState(false);
  const [failedDocuments, setFailedDocuments] = useState(null);

  const create = (dataToSend) => {
    setFailedDocuments(null);
    dispatch(createConsentTypeAction(dataToSend))
      .then((resp) => {
        if (resp?.status === 200) {
          handleClose();
        } else {
          if (resp?.data?.invalidFiles) {
            setFailedDocuments(resp.data.invalidFiles)
          }
        }
      });
  }

  const modify = (dataToSend) => {
    setFailedDocuments(null);
    dispatch(updateConsentTypeAction({ internalCode: data.internalCode, ...dataToSend }))
      .then((resp) => {
        if (resp?.status === 200) {
          handleClose();
        } else {
          if (resp?.data?.invalidFiles) {
            setFailedDocuments(resp.data.invalidFiles)
          }
        }
      });
  }

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const openValidationFileError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("templateIsRequiredWhenInstanceIsSelected"), severity: "error", duration: 10000 }));
  }

  const {
    isCreate,
    formData,
    setFormValue,
    submit,
  } = useHandleForm(data, create, modify, openValidationError, openValidationFileError);

  const handleOpenGallery = () => {
    setImagesDialogIsOpen(true);
  }

  const handleSelectImage = (image) => {
    setFormValue(image, "image");
  }

  const typeArrToUse = (formData.instance && formData.instance !== '-') ? types.find(item => item.key === formData.instance).values : [];

  return (
    <Grid container item xs={12} direction="row" style={{ paddingTop: 10 }}>
      <Grid container item xs={12}>
        <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}> {/* codigo */}
          <TextField
            disabled={!Boolean(isCreate)}
            required
            fullWidth
            margin={"none"}
            label={getLocalizedString("centerCode")}
            onChange={(e) => setFormValue(e.target.value, "code")}
            value={formData.code}
            inputProps={{ maxLength: 8 }}
          />
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}> {/* nombre */}
          <TextField
            required
            fullWidth
            margin={"none"}
            label={getLocalizedString("name")}
            value={formData.name}
            onChange={(e) => setFormValue(e.target.value, "name")}
            inputProps={{ maxLength: 40 }}
          />
        </Grid>
        <Grid container item alignItems="center" xs={12} style={{ ...gridStyle, height: '' }}> {/* texto */}
          <TextFieldMultiline
            required
            fullWidth
            margin={"none"}
            label={getLocalizedString("publicationText")}
            onChange={(e) => setFormValue(e.target.value, "text")}
            value={formData.text}
            rows={25}
            inputProps={{ spellCheck: 'false' }}
          />
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* Link o Enlace */}
          <TextField
            fullWidth
            margin={"none"}
            label={getLocalizedString("publicationLink")}
            value={formData.link}
            onChange={(e) => setFormValue(e.target.value, "link")}
            inputProps={{ maxLength: 254 }}
          />
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* enableAlert */}
          <FormControl style={{ width: "100%" }}>
            <InputLabel required id={`label-enable`}>{getLocalizedString("enableAlertMessage")}</InputLabel>
            <Select
              value={formData.enableAlert}
              labelId={`label-enable`}
              id={`select-enable`}
              onChange={(e) => setFormValue(e.target.value, "enableAlert")}
              margin={"none"}
            >
              {
                [{ key: "Y", value: getLocalizedString("yes") }, { key: "N", value: getLocalizedString("no") }].map(item => (
                  <MenuItem
                    className={"whiteText"}
                    key={item.key}
                    value={item.key}>
                    {item.value}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* instancee */}
          <FormControl style={{ width: "100%" }}>
            <InputLabel id={`label-instance`}>{getLocalizedString("digitalFolderInstance")}</InputLabel>
            <Select
              value={formData.instance}
              labelId={`label-instance`}
              id={`select-instance`}
              onChange={(e) => setFormValue(e.target.value, "instance")}
              margin={"none"}
            >
              {
                instances?.map(item => (
                  <MenuItem
                    className={"whiteText"}
                    key={item.key}
                    value={item.key}>
                    {item.value}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Grid>
        <Grid container item alignItems="center" xs={12} sm={6} md={3} style={gridStyle}> {/* type */}
          <FormControl style={{ width: "100%" }}>
            <InputLabel required={formData.instance !== "-"} id={`label-type`}>{getLocalizedString("digitalFolderFileType")}</InputLabel>
            <Select
              disabled={formData.instance === "-"}
              value={formData.type}
              labelId={`label-type`}
              id={`select-type`}
              onChange={(e) => setFormValue(e.target.value, "type")}
              margin={"none"}
            >
              {
                typeArrToUse?.map(item => (
                  <MenuItem
                    className={"whiteText"}
                    key={item.key}
                    value={item.key}>
                    {item.value}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Grid>
        <Grid container item alignItems="center" xs={12} style={gridStyle}> {/* template */}
          <FileInput
            acceptedFormats={['.html']}
            handleChange={(files) => setFormValue(files, 'template')}
            id={`poll${data?.code || 'create'}`}
            preSubmitCallback={() => setFailedDocuments(null)}
          />
          <FileList
            files={formData.template}
            filesWithError={failedDocuments}
            handleClick={() => setFormValue(null, 'template')}
          />
          {
            Boolean(data?.template && !formData.template?.length) &&
            <Grid item xs={12} style={{ marginTop: 5 }}>
              <Typography className="whiteText" style={{ fontSize: 16 }}>
                {`${getLocalizedString("loadedTemplate")}: ${data?.template}`}
              </Typography>
            </Grid>
          }
          {
            Boolean(!data?.template && !formData.template?.length) &&
            <Grid item xs={12} style={{ marginTop: 5 }}>
              <Typography className="whiteText" style={{ fontSize: 16 }}>
                {getLocalizedString("currentLoadedTemplateNoTemplateLoaded")}
              </Typography>
            </Grid>
          }
        </Grid>
        <Grid container item alignItems='center' xs={12} style={{ height: 200, padding: "10px 10px", paddingBottom: 20 }}>
          <GalleryButtonAndDisplay
            image={formData.image}
            handleOpenGallery={handleOpenGallery}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} justify={"flex-end"} alignItems="center" style={{ paddingBottom: 20 }}>
        <ButtonDialogAction isAccept onClick={submit} text={getLocalizedString("save")} />
      </Grid>
      {
        imagesDialogIsOpen &&
        <GalleryDialog
          open={imagesDialogIsOpen}
          initialValue={formData.image}
          type={"news"}
          handleSubmit={(img) => handleSelectImage(img)}
          handleClose={() => setImagesDialogIsOpen(false)}
        />
      }
    </Grid>
  );
}

export default Form;